<template>
  <v-card v-if="project" height="100%" class="pa-0">
    <v-card-title>
      <v-layout row>
        <v-flex shrink>
          <v-avatar size=48>
            <img :src="logoThumbUrl" alt="Avatar" v-if="logoThumbUrl">
            <v-icon color="primary" large v-else>ballot</v-icon>
          </v-avatar>
        </v-flex>
        <v-flex class="text-truncate pt-3">
          <router-link class="subheading" :to="projectRoute">{{project.attributes.name}}</router-link>
          <br>
          {{project.attributes.description}}
        </v-flex>

        <v-flex text-xs-right shrink class="text-no-wrap">
          <v-btn icon @click="unsetWatch()" class="ma-0" v-if="watch">
            <v-icon small class="mr-1">visibility</v-icon>
          </v-btn>
          <v-btn icon @click="setWatch()" class="ma-0" v-else>
            <v-icon small class="mr-1" color="grey">visibility_off</v-icon>
          </v-btn>

          <v-menu class="pl-0" v-show="permit.canCreateBoard(project) || permit.canCreateTicket(project)" >
            <v-btn slot="activator" icon class="ma-0">
              <v-icon color="darkgrey">more_vert</v-icon>
            </v-btn>
            <v-list>
              <v-list-tile @click="newBoard = !newBoard" v-if="permit.canCreateBoard(project)">
                <v-list-tile-title>
                  <v-icon color="darkgrey" small class="mr-1">view_carousel</v-icon>
                    Create new board
                </v-list-tile-title>
              </v-list-tile>
              <v-list-tile @click="newTicket = !newTicket" v-if="permit.canCreateTicket(project)">
                <v-list-tile-title>
                  <v-icon color="darkgrey" small class="mr-1">local_play</v-icon>
                    Create new ticket
                </v-list-tile-title>
              </v-list-tile>
              <v-list-tile @click="editProject = !editProject" v-if="permit.canEditProject(project)">
                <v-list-tile-title>
                  <v-icon color="darkgrey" small class="mr-1">edit</v-icon>
                    Edit Project
                </v-list-tile-title>
              </v-list-tile>
              <v-list-tile @click="newPurchase = !newPurchase" v-if="permit.canCreatePurchase(project)">
                <v-list-tile-title>
                  <v-icon color="darkgrey" small class="mr-1">wallet</v-icon>
                  Seats
                </v-list-tile-title>
              </v-list-tile>

              <v-divider v-if="permit.canDeleteProject(project)"></v-divider>
              <v-list-tile @click="deleteProject = !deleteProject" v-if="permit.canDeleteProject(project)">
                <v-list-tile-title>
                  <v-icon color="darkgrey" small class="mr-1">delete</v-icon>
                    Delete Project
                </v-list-tile-title>
              </v-list-tile>
            </v-list>
          </v-menu>
        </v-flex>
      </v-layout>
    </v-card-title>

    <v-card-text>
      <p class="text-subtext mb-2" v-if="lastActive">Last active {{ lastActive|formatDateAgo }} </p>
      <p class="text-subtext mb-2" v-else>No activity</p>
      <p class="text-subtext mb-0">{{assignedTicketCount}} assigned to you</p>
    </v-card-text>

    <v-layout row wrap justify-space-around pl-3>
      <v-flex>
        <router-link :to="backlogRoute">
          <v-icon color="darkgrey" class="pr-1">view_carousel</v-icon><span>{{ boards ? boards.length : 0 }}</span>
        </router-link>
      </v-flex>

      <v-flex>
        <router-link :to="projectRoute">
          <v-icon color="darkgrey" class="pr-1">local_play</v-icon><span>{{ ticketTotal }}</span>
        </router-link>
      </v-flex>

      <v-flex>
        <router-link :to="projectRoute">
          <v-icon color="darkgrey" class="pr-1">group</v-icon><span>{{ members ? members.length : 0 }}</span>
        </router-link>
      </v-flex>

      <v-flex>
        <router-link :to="emailRoute">
          <v-icon color="darkgrey" class="pr-1">email</v-icon><span>{{ emailTotal }}</span>
        </router-link>
      </v-flex>
    </v-layout>


    <v-dialog
      :fullscreen="$vuetify.breakpoint.xsOnly"
      v-model="editProject"
      max-width="75%"
      persistent
      lazy
      >
      <projectEdit :project="project" ></projectEdit>
    </v-dialog>

    <v-dialog
      v-model="deleteProject"
      max-width="500px"
      persistent
      lazy
      :fullscreen="$vuetify.breakpoint.xsOnly"
      >
      <projectDelete :project="project" ></projectDelete>
    </v-dialog>

    <v-dialog
      v-model="newBoard"
      max-width="500px"
      persistent
      lazy
      :fullscreen="$vuetify.breakpoint.xsOnly"
      >
      <boardNew :project="project"></boardNew>
    </v-dialog>

    <v-dialog
      v-model="newTicket"
      max-width="65%"
      persistent
      lazy
      :fullscreen="$vuetify.breakpoint.xsOnly"
      >
      <ticketNew :project="project" :boards="boards" :statuses="statuses" :members="members"></ticketNew>
    </v-dialog>

    <v-dialog
      :fullscreen="$vuetify.breakpoint.xsOnly"
      v-model="newPurchase"
      max-width="65%"
      persistent
      lazy
      >
      <purchaseNew :project="project" ></purchaseNew>
    </v-dialog>
  </v-card>
</template>

<script>
  import { EventBus } from '@/utilities/event-bus'
  import authentication from '@/utilities/authentication'
  import permissions from '@/utilities/permissions'

  import projectEdit from '@/components/project/_edit'
  import projectDelete from '@/components/project/_delete'

  import boardService from '@/services/board'
  import boardNew from '@/components/board/_new'

  import ticketNew from '@/components/ticket/_new'

  import purchaseNew from '@/components/purchase/_new'

  import statusService from '@/services/status'
  import memberService from '@/services/member'
  import watchService from '@/services/watch'

  export default {
    name: 'project-card',

    components: {
      projectEdit,
      projectDelete,
      boardNew,
      ticketNew,
      purchaseNew
    },

    props: ['project'],

    data () {
      return {
        auth: authentication,
        permit: permissions,
        editProject: false,
        seatProject: false,
        deleteProject: false,
        newPurchase: false,
        boards: [],
        newBoard: false,
        ticketTotal: 0,
        emailTotal: 0,
        newTicket: false,
        statuses: [],
        members: [],
        watch: null
      }
    },

    mounted () {
      this.ticketTotal = parseInt(this.project.attributes.ticket_count)
      this.emailTotal = parseInt(this.project.attributes.email_count)

      this.loadProjectBoards()
      this.loadProjectStatuses()
      this.loadProjectMembers()
      this.loadProjectWatches()

      EventBus.$on('board-created', (payload) => {
        if (payload && (parseInt(payload.relationships.project.data.id) === parseInt(this.project.id))) {
          this.boards.push(payload)
        }
        this.newBoard = false
      })

      EventBus.$on('ticket-created', (payload) => {
        this.ticketTotal = this.ticketTotal + 1
        this.newTicket = false
      })

      EventBus.$on('project-updated', (payload) => {
        // the project is updated in the index page, where this card is seeded with the 'project'
        this.editProject = false
      })

      EventBus.$on('project-deleted', (payload) => {
        // we dont do anything if the user cancelled the delete, processing the deletion is done in the project index
        this.deleteProject = false
      })

      EventBus.$on('project-purchase', (payload) => {
        this.newPurchase = false
      })
    },

    computed: {
      projectRoute () {
        if (this.project) {
          return { path: '/projects/' + this.project.id }
        }
        return ''
      },

      backlogRoute () {
        if (this.project) {
          return { path: '/projects/' + this.project.id + '/backlog' }
        }
        return ''
      },

      emailRoute () {
        if (this.project) {
          return { path: '/projects/' + this.project.id + '/emails' }
        }
        return ''
      },

      assignedTicketCount () {
        let count = parseInt(this.project.attributes.assigned_ticket_count || 0)
        if (count === 1) {
          return count + ' ticket is'
        } else if (count > 1) {
          return count + ' tickets are'
        } else {
          return 'No tickets are'
        }
      },

      lastActive () {
        return this.project.attributes.last_active_on
      },

      logoThumbUrl () {
        if (this.project.links && this.project.links.logo_thumb_uri) {
          return (process.env.API_URL + this.project.links.logo_thumb_uri)
        }
        return null
      }
    },

    methods: {
      loadProjectBoards () {
        boardService.projectIndex(
          (data) => {
            this.boards = data.filter(brd => !brd.attributes.archived)
            this.boards.sort((a, b) => { return a.attributes.name.toLowerCase().localeCompare(b.attributes.name.toLowerCase()) })
          },
          (err) => {
            console.log(err)
          },
          this.project.id
        )
      },

      loadProjectStatuses () {
        statusService.projectIndex(
          (data) => {
            this.statuses = data.sort((a, b) => { return parseInt(a.position) - parseInt(b.position) })
          },
          (err) => {
            console.log(err)
          },
          this.project.id
        )
      },

      loadProjectMembers () {
        memberService.projectIndex(
          (data) => {
            this.members = data.filter(m => !m.attributes.discarded_at).sort((a, b) => { return a.attributes.name.toLowerCase().localeCompare(b.attributes.name.toLowerCase()) })
          },
          (err) => {
            console.log(err)
          },
          this.project.id
        )
      },

      loadProjectWatches () {
        watchService.projectIndex(
          (data) => {
            // this will return ALL the watches under that project, including those for boards and tickets
            this.watch = data.find(watch => this.auth.isCurrentUser(watch.relationships.user.data.id) && (watch.relationships.watchable.data.type === 'project'))
          },
          (err) => {
            console.log(err)
          },
          this.project.id
        )
      },

      setWatch () {
        watchService.projectCreate(
          (data) => {
            this.watch = data
          },
          (err) => {
            console.log(err)
          },
          this.project.id,
          {user_id: this.auth.userId()}
        )
      },

      unsetWatch () {
        watchService.projectDelete(
          (data) => {
            this.watch = null
          },
          (err) => {
            console.log(err)
          },
          this.project.id,
          this.watch.id
        )
      }
    }
  }
</script>

<style lang="scss" scoped>
  .v-avatar  {
    i {
      border: 2px solid lightgrey !important;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/project/_card.vue